'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdCleaningServices,
    MdPlayArrow,
    MdCheck,
    MdVerified,
} from 'react-icons/md'
import {
    getCleanings,
    requestCleaning,
    startCleaning,
    completeCleaning,
    verifyCleaning,
    BedCleaning,
    CLEANING_STATUS_LABELS,
    CLEANING_STATUS_COLORS,
    CLEANING_TYPE_LABELS,
    CleaningType,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function CleaningPage() {
    const [cleanings, setCleanings] = useState<BedCleaning[]>([])
    const [loading, setLoading] = useState(true)
    const [showNewModal, setShowNewModal] = useState(false)
    const [newCleaning, setNewCleaning] = useState({
        bedId: 0,
        type: 'TERMINAL' as CleaningType,
        notes: '',
    })
    const [saving, setSaving] = useState(false)
    const { hospitals, loading: loadingHospitals, selectedHospitalId, setSelected } = useHospitals()

    useEffect(() => {
        loadCleanings()
    }, [selectedHospitalId])

    const loadCleanings = async () => {
        setLoading(true)
        try {
            const response = await getCleanings(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setCleanings(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar higienizações')
            }
        } catch (error) {
            toast.error('Erro ao carregar higienizações')
        } finally {
            setLoading(false)
        }
    }

    const handleRequestCleaning = async () => {
        if (!newCleaning.bedId) {
            toast.error('Selecione um leito')
            return
        }

        setSaving(true)
        try {
            const response = await requestCleaning(newCleaning)
            if (response.success) {
                toast.success('Higienização solicitada com sucesso')
                setShowNewModal(false)
                setNewCleaning({ bedId: 0, type: 'TERMINAL', notes: '' })
                loadCleanings()
            } else {
                toast.error(response.error || 'Erro ao solicitar higienização')
            }
        } catch (error) {
            toast.error('Erro ao solicitar higienização')
        } finally {
            setSaving(false)
        }
    }

    const handleStart = async (id: number) => {
        try {
            const response = await startCleaning(id)
            if (response.success) {
                toast.success('Higienização iniciada')
                loadCleanings()
            } else {
                toast.error(response.error || 'Erro ao iniciar higienização')
            }
        } catch (error) {
            toast.error('Erro ao iniciar higienização')
        }
    }

    const handleComplete = async (id: number) => {
        try {
            const response = await completeCleaning(id)
            if (response.success) {
                toast.success('Higienização concluída')
                loadCleanings()
            } else {
                toast.error(response.error || 'Erro ao concluir higienização')
            }
        } catch (error) {
            toast.error('Erro ao concluir higienização')
        }
    }

    const handleVerify = async (id: number) => {
        try {
            const response = await verifyCleaning(id)
            if (response.success) {
                toast.success('Higienização verificada')
                loadCleanings()
            } else {
                toast.error(response.error || 'Erro ao verificar higienização')
            }
        } catch (error) {
            toast.error('Erro ao verificar higienização')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando higienizações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg">
                        <MdCleaningServices className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Higienização
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie solicitações de limpeza de leitos
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => setShowNewModal(true)}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Solicitar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
            </div>

            {cleanings.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdCleaningServices className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma higienização pendente
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Todas as solicitações foram atendidas
                        </p>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Leito</th>
                                <th>Tipo</th>
                                <th>Solicitado</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cleanings.map(cleaning => (
                                <tr key={cleaning.id}>
                                    <td>
                                        <div className="font-bold">
                                            Leito {cleaning.bed?.number}
                                        </div>
                                        <div className="text-sm text-base-content/60">
                                            {cleaning.bed?.unit?.name}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {CLEANING_TYPE_LABELS[cleaning.type]}
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(cleaning.requestedAt).toLocaleString(
                                            'pt-BR'
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${CLEANING_STATUS_COLORS[cleaning.status]}`}
                                        >
                                            {CLEANING_STATUS_LABELS[cleaning.status]}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            {cleaning.status === 'CLEAN_REQUESTED' && (
                                                <button
                                                    className="btn btn-secondary btn-sm gap-1"
                                                    onClick={() =>
                                                        handleStart(cleaning.id)
                                                    }
                                                >
                                                    <MdPlayArrow />
                                                    Iniciar
                                                </button>
                                            )}
                                            {cleaning.status === 'CLEAN_IN_PROGRESS' && (
                                                <button
                                                    className="btn btn-success btn-sm gap-1"
                                                    onClick={() =>
                                                        handleComplete(cleaning.id)
                                                    }
                                                >
                                                    <MdCheck />
                                                    Concluir
                                                </button>
                                            )}
                                            {cleaning.status === 'CLEAN_COMPLETED' && (
                                                <button
                                                    className="btn btn-info btn-sm gap-1"
                                                    onClick={() =>
                                                        handleVerify(cleaning.id)
                                                    }
                                                >
                                                    <MdVerified />
                                                    Verificar
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* New Cleaning Modal */}
            <dialog className={`modal ${showNewModal ? 'modal-open' : ''}`}>
                <div className="modal-box !max-w-md">
                    <h3 className="font-bold text-lg mb-4">Solicitar Higienização</h3>
                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    ID do Leito *
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                placeholder="ID do leito"
                                value={newCleaning.bedId || ''}
                                onChange={e =>
                                    setNewCleaning({
                                        ...newCleaning,
                                        bedId: parseInt(e.target.value) || 0,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Tipo *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={newCleaning.type}
                                onChange={e =>
                                    setNewCleaning({
                                        ...newCleaning,
                                        type: e.target.value as CleaningType,
                                    })
                                }
                            >
                                {Object.entries(CLEANING_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                placeholder="Observações..."
                                value={newCleaning.notes}
                                onChange={e =>
                                    setNewCleaning({
                                        ...newCleaning,
                                        notes: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setShowNewModal(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleRequestCleaning}
                            disabled={saving}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                'Solicitar'
                            )}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowNewModal(false)}>close</button>
                </form>
            </dialog>
        </div>
    )
}
